// IVR call log data + reporting aggregation. The IVR dev's Supabase writer
// currently saves call metadata and a full transcript per call
// (ivr_call_logs) -- there's no recording-audio column/bucket yet, so this
// deliberately doesn't reference one; the Call Log UI says so explicitly
// rather than shipping a dead "play" button.
import { supabase } from "@/lib/supabase";
import { format, startOfWeek, startOfMonth, addDays, addWeeks, addMonths, subDays } from "date-fns";
import { formatDate } from "@/lib/dates";

export interface IvrCallLog {
  id: string;
  tenant_id: string;
  call_id: string;
  caller_phone: string | null;
  bookings_started: number;
  bookings_completed: number;
  transfer_reason: string | null;
  started_at: string;
  ended_at: string;
  transcript: string | null;
}

export async function fetchIvrCallLogs(): Promise<IvrCallLog[]> {
  // Supabase caps a single request at 1000 rows, so page through everything --
  // otherwise the log and reporting silently drop the oldest calls.
  const PAGE = 1000;
  const all: IvrCallLog[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("ivr_call_logs")
      .select("*")
      .order("started_at", { ascending: false })
      .order("id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) {
      console.error("Error fetching IVR call logs:", error);
      return all;
    }
    all.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return all;
}

export interface LinkedCrmAccount {
  account_id: string;
  account_name: string;
  is_active: boolean;
}

// Caller IDs arrive as local NZ numbers (0211234567) but CRM phones are
// hand-typed, so compare on digits only with +64/64 folded back to a leading 0.
export function normalizePhoneForMatch(raw: string | null | undefined): string {
  if (!raw) return "";
  const cleaned = raw.replace(/[^0-9+]/g, "");
  const local = cleaned.replace(/^\+?64/, "0");
  return /^[1-9]/.test(local) ? `0${local}` : local;
}

// Map of normalised phone -> CRM accounts using it (phone, traveller phone/mobile
// or billing phone). A number can belong to several accounts (shared office
// line etc.), so active accounts sort first.
export async function fetchCrmAccountsByPhone(): Promise<Map<string, LinkedCrmAccount[]>> {
  const map = new Map<string, LinkedCrmAccount[]>();
  // Supabase caps a single request at 1000 rows and there are thousands of
  // accounts, so page through them all (ordered so pages don't overlap).
  const PAGE = 1000;
  const rows: {
    account_id: string;
    account_name: string;
    is_active: boolean | null;
    phone: string | null;
    traveller_phone: string | null;
    traveller_mobile: string | null;
    billing_phone: string | null;
  }[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("crm_accounts")
      .select("account_id, account_name, is_active, phone, traveller_phone, traveller_mobile, billing_phone")
      .order("account_id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) {
      console.error("Error fetching CRM accounts for IVR call log:", error);
      return map;
    }
    rows.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  for (const row of rows) {
    const account: LinkedCrmAccount = {
      account_id: String(row.account_id),
      account_name: row.account_name,
      is_active: !!row.is_active,
    };
    const seen = new Set<string>();
    for (const p of [row.phone, row.traveller_phone, row.traveller_mobile, row.billing_phone]) {
      const key = normalizePhoneForMatch(p);
      // Very short values (e.g. "027339") are partial numbers, not real caller IDs.
      if (key.length < 8 || seen.has(key)) continue;
      seen.add(key);
      const list = map.get(key) || [];
      list.push(account);
      map.set(key, list);
    }
  }
  map.forEach((list) => list.sort((a, b) => Number(b.is_active) - Number(a.is_active)));
  return map;
}

// account_ids that have at least one active "home" address on file
// (crm_account_addresses). The billing address on crm_accounts is unused, so
// this table is the only real home-address source.
export async function fetchAccountIdsWithHomeAddress(): Promise<Set<string>> {
  const ids = new Set<string>();
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("crm_account_addresses")
      .select("account_id, full_address, address_line_1")
      .eq("address_type", "home")
      .eq("is_active", true)
      .order("id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) {
      console.error("Error fetching CRM home addresses:", error);
      return ids;
    }
    for (const row of data || []) {
      if ((row.full_address || "").trim() || (row.address_line_1 || "").trim()) ids.add(String(row.account_id));
    }
    if (!data || data.length < PAGE) break;
  }
  return ids;
}

// No explicit "abandoned"/"transferred" flag exists on the row -- these are
// inferred from the fields the IVR dev does send. A transfer no longer
// swallows everything else that happened on the call (a booking CAN be
// completed and the caller still gets transferred afterwards -- e.g. for
// something the IVR can't help with -- and that's a genuinely different,
// better outcome than a transfer with nothing else attempted), so
// "transferred" now splits into three, by what happened before the
// transfer: a booking completed, a booking started but not finished, or
// the caller asked for a human straight away with no attempt at all.
export type CallOutcome =
  | "booked"
  | "transferred_booked"
  | "transferred_attempted"
  | "transferred_immediate"
  | "started_not_completed"
  | "abandoned";

export const OUTCOME_LABEL: Record<CallOutcome, string> = {
  booked: "Completed Booking",
  transferred_booked: "Transferred (Booking Completed)",
  transferred_attempted: "Transferred (Booking Attempted)",
  transferred_immediate: "Transferred (Asked Immediately)",
  started_not_completed: "Started, Not Completed",
  abandoned: "No Booking / Hung Up",
};

export function wasTransferred(call: IvrCallLog): boolean {
  return !!(call.transfer_reason && call.transfer_reason.trim());
}

export function classifyOutcome(call: IvrCallLog): CallOutcome {
  if (wasTransferred(call)) {
    if (call.bookings_completed > 0) return "transferred_booked";
    if (call.bookings_started > 0) return "transferred_attempted";
    return "transferred_immediate";
  }
  if (call.bookings_completed > 0) return "booked";
  if (call.bookings_started > 0) return "started_not_completed";
  return "abandoned";
}

export function callDurationSeconds(call: IvrCallLog): number {
  const ms = new Date(call.ended_at).getTime() - new Date(call.started_at).getTime();
  return Math.max(0, Math.round(ms / 1000));
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export type Granularity = "day" | "week" | "month";

export interface VolumePoint {
  key: string;
  label: string;
  count: number;
}

// Shared bucket-key logic so the trend chart (callVolumeByGranularity) and
// the period selector (periodRange/anchorBucketKey below) can never drift
// out of sync on what counts as "the same day/week/month".
function bucketKeyAndLabel(granularity: Granularity, d: Date): { key: string; label: string } {
  if (granularity === "day") {
    return { key: format(d, "yyyy-MM-dd"), label: formatDate(d) };
  }
  if (granularity === "week") {
    const weekStart = startOfWeek(d, { weekStartsOn: 1 });
    return { key: format(weekStart, "yyyy-MM-dd"), label: `Wk of ${formatDate(weekStart)}` };
  }
  const monthStart = startOfMonth(d);
  // Spelled-out month/year, not formatDate -- see ActualPerformanceTab.tsx.
  return { key: format(monthStart, "yyyy-MM"), label: format(monthStart, "MMM yyyy") };
}

// Buckets by the browser's local time (assumed NZ, same convention as the
// rest of this codebase's date displays -- see formatNZDateTime in
// VehicleInspection.tsx) so a late-night call lands on the calendar day
// callers actually experienced, not its UTC date.
export function callVolumeByGranularity(calls: IvrCallLog[], granularity: Granularity): VolumePoint[] {
  const buckets = new Map<string, { label: string; count: number }>();
  for (const call of calls) {
    const d = new Date(call.started_at);
    const { key, label } = bucketKeyAndLabel(granularity, d);
    const existing = buckets.get(key);
    if (existing) existing.count += 1;
    else buckets.set(key, { label, count: 1 });
  }
  return Array.from(buckets.entries())
    .map(([key, v]) => ({ key, label: v.label, count: v.count }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

// The selected reporting period -- an arbitrary anchor date snapped to the
// day/week(Mon-start)/month it falls in. `end` is exclusive, so callers can
// use it directly as a `< end` bound against timestamp columns.
export interface PeriodRange {
  start: Date;
  end: Date;
  label: string;
}

export function periodRange(granularity: Granularity, anchor: Date): PeriodRange {
  if (granularity === "day") {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    return { start, end: addDays(start, 1), label: `${format(start, "EEEE")} ${formatDate(start)}` };
  }
  if (granularity === "week") {
    const start = startOfWeek(anchor, { weekStartsOn: 1 });
    const end = addWeeks(start, 1);
    return { start, end, label: `${formatDate(start)} – ${formatDate(subDays(end, 1))}` };
  }
  const start = startOfMonth(anchor);
  // Spelled-out month/year, not formatDate -- see ActualPerformanceTab.tsx.
  return { start, end: addMonths(start, 1), label: format(start, "MMMM yyyy") };
}

// Moves the anchor by one period in either direction -- used by the Prev/Next
// navigation controls in IvrCallReportingTab.
export function shiftAnchor(granularity: Granularity, anchor: Date, direction: 1 | -1): Date {
  if (granularity === "day") return addDays(anchor, direction);
  if (granularity === "week") return addWeeks(anchor, direction);
  return addMonths(anchor, direction);
}

export function anchorBucketKey(granularity: Granularity, anchor: Date): string {
  return bucketKeyAndLabel(granularity, anchor).key;
}

// Inverse of anchorBucketKey/bucketKeyAndLabel -- turns a VolumePoint's `key`
// (clicked on in the Call Volume chart) back into a Date usable as an anchor.
export function parseBucketKey(granularity: Granularity, key: string): Date {
  const parts = key.split("-").map(Number);
  if (granularity === "month") return new Date(parts[0], parts[1] - 1, 1);
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

export interface HourPoint {
  hour: number;
  label: string;
  count: number;
}

export function callVolumeByHour(calls: IvrCallLog[]): HourPoint[] {
  const counts = new Array(24).fill(0);
  for (const call of calls) {
    const h = new Date(call.started_at).getHours();
    counts[h] += 1;
  }
  return counts.map((count, hour) => ({
    hour,
    label: hour === 0 ? "12am" : hour < 12 ? `${hour}am` : hour === 12 ? "12pm" : `${hour - 12}pm`,
    count,
  }));
}

export interface OutcomeCount {
  outcome: CallOutcome;
  label: string;
  count: number;
  pct: number;
}

const OUTCOME_ORDER: CallOutcome[] = [
  "booked",
  "transferred_booked",
  "transferred_attempted",
  "transferred_immediate",
  "started_not_completed",
  "abandoned",
];

export function outcomeCounts(calls: IvrCallLog[]): OutcomeCount[] {
  const counts: Record<CallOutcome, number> = {
    booked: 0,
    transferred_booked: 0,
    transferred_attempted: 0,
    transferred_immediate: 0,
    started_not_completed: 0,
    abandoned: 0,
  };
  calls.forEach((c) => {
    counts[classifyOutcome(c)] += 1;
  });
  const total = calls.length || 1;
  return OUTCOME_ORDER.map((outcome) => ({
    outcome,
    label: OUTCOME_LABEL[outcome],
    count: counts[outcome],
    pct: Math.round((counts[outcome] / total) * 100),
  }));
}

export interface CallOutcomeSummary {
  totalBooked: number; // completed a booking, whether or not also transferred
  totalTransferred: number; // transferred, regardless of booking outcome
  transferredImmediate: number; // asked for the operator with no booking attempt at all
  transferredButBooked: number; // the "dual outcome" case -- transferred AND booked
}

// Top-line counts for the stat tiles -- these overlap on purpose (a
// transferred-and-booked call counts in both totalBooked and
// totalTransferred), unlike outcomeCounts()'s buckets, which are mutually
// exclusive and sum to 100%. Answers "how many calls actually resulted in
// a booking" and "how many callers asked for a human without trying
// anything first" directly, instead of leaving them buried inside a
// single "transferred" bucket.
export function outcomeSummary(calls: IvrCallLog[]): CallOutcomeSummary {
  let totalBooked = 0;
  let totalTransferred = 0;
  let transferredImmediate = 0;
  let transferredButBooked = 0;
  calls.forEach((c) => {
    const booked = c.bookings_completed > 0;
    const transferred = wasTransferred(c);
    if (booked) totalBooked += 1;
    if (transferred) totalTransferred += 1;
    if (transferred && booked) transferredButBooked += 1;
    if (transferred && c.bookings_started === 0) transferredImmediate += 1;
  });
  return { totalBooked, totalTransferred, transferredImmediate, transferredButBooked };
}

// Total bookings placed for a period, for comparing IVR volume against
// everything that came in (dispatcher-entered, forward bookings, other
// channels). taxicaller_data has no "booking placed" timestamp -- TaxiCaller's
// Forward Jobs report exposes "Filed" but sync-forward-jobs-from-taxicaller
// doesn't currently capture it -- so this counts by requested_pickup_at (the
// trip's scheduled/requested time) as the closest available proxy for "jobs
// that came in" on a given day. Fine for same-day/ASAP-heavy comparison
// against IVR call volume; a forward booking made weeks ahead lands in its
// pickup day's bucket, not the day it was actually booked.
export async function fetchJobsCountForRange(start: Date, end: Date): Promise<number> {
  const { count, error } = await supabase
    .from("taxicaller_data")
    .select("Job_ID", { count: "exact", head: true })
    .gte("requested_pickup_at", start.toISOString())
    .lt("requested_pickup_at", end.toISOString());
  if (error) {
    console.error("Error fetching taxicaller_data job count:", error);
    return 0;
  }
  return count ?? 0;
}
